import {
  ClaimCitationIssued,
  type ClaimDriverInfo,
  ClaimHowManyVehicles,
  type ClaimOtherVehicle,
  type ClaimTrailer,
  ClaimTrailerOwnership,
  type ClaimTruck,
  ClaimWhatDamaged,
  ClaimWhatHappened,
  ReporterRelationship,
} from '../../../../models/claim'
import { INJURED_PARTY_OPTIONS } from './ClaimInjuriesSection'
import { RELATIONSHIP_OPTIONS } from './ClaimStep2Fields'
import { DRIVEABLE_OPTIONS, LOAD_OPTIONS, TOW_OPTIONS } from './ClaimVehicleStatusSection'

export const WHAT_HAPPENED_LABELS: Record<string, string> = {
  [ClaimWhatHappened.TRUCK_HIT_VEHICLE]: 'My truck hit another vehicle',
  [ClaimWhatHappened.VEHICLE_HIT_TRUCK]: 'Another vehicle hit my truck',
  [ClaimWhatHappened.ROLLOVER]: 'My truck rolled over',
  [ClaimWhatHappened.HIT_OBJECT]: 'I hit something (not a vehicle)',
  [ClaimWhatHappened.CARGO_DAMAGED_LOST]: 'My cargo was damaged or lost',
  [ClaimWhatHappened.INJURY]: 'Someone was injured',
  [ClaimWhatHappened.THEFT_VANDALISM]: 'My truck was stolen or vandalized',
  [ClaimWhatHappened.FIRE_EXPLOSION]: 'Fire or explosion',
  [ClaimWhatHappened.WEATHER]: 'Weather damage',
  [ClaimWhatHappened.OTHER]: 'Something else',
}

export const HOW_MANY_VEHICLES_LABELS: Record<string, string> = {
  [ClaimHowManyVehicles.TWO_OR_MORE]: '2 or more vehicles',
  [ClaimHowManyVehicles.MY_VEHICLE_ONLY]: 'My vehicle only',
  [ClaimHowManyVehicles.NO_VEHICLES]: 'No vehicles',
}

export const WHAT_DAMAGED_LABELS: Record<string, string> = {
  [ClaimWhatDamaged.MY_TRACTOR]: 'My tractor',
  [ClaimWhatDamaged.MY_TRAILER]: 'My trailer',
  [ClaimWhatDamaged.OTHER_VEHICLE]: 'The other vehicle',
  [ClaimWhatDamaged.PROPERTY]: 'Property',
  [ClaimWhatDamaged.MY_CARGO]: 'My cargo',
  [ClaimWhatDamaged.PERSONAL_VEHICLE]: 'Personal vehicle',
}

export const TRAILER_OWNERSHIP_LABELS: Record<string, string> = {
  [ClaimTrailerOwnership.OWNED_NOT_LISTED]: 'We own it',
  [ClaimTrailerOwnership.LEASED]: 'Leased',
  [ClaimTrailerOwnership.THIRD_PARTY_CARRIER]: 'Third-party / carrier',
  [ClaimTrailerOwnership.UNKNOWN]: 'Unknown',
}

/** Labels read straight off the wizard's options, so the summary repeats what the reporter picked. */
function labelsOf(options: { value: string; label: string }[]): Record<string, string> {
  return Object.fromEntries(options.map((option) => [option.value, option.label]))
}

export const DRIVEABLE_LABELS = labelsOf(DRIVEABLE_OPTIONS)
export const TOW_LABELS = labelsOf(TOW_OPTIONS)
export const LOAD_STATUS_LABELS = labelsOf(LOAD_OPTIONS)
export const DRIVER_RELATIONSHIP_LABELS = labelsOf(RELATIONSHIP_OPTIONS)
export const INJURED_PARTY_LABELS = labelsOf(INJURED_PARTY_OPTIONS)

export const CITATION_LABELS: Record<string, string> = {
  [ClaimCitationIssued.NONE]: 'No one',
  [ClaimCitationIssued.OUR_DRIVER]: 'Our driver',
  [ClaimCitationIssued.OTHER_DRIVER]: 'Other driver',
  [ClaimCitationIssued.UNKNOWN]: 'Unknown',
}

export const REPORTER_RELATIONSHIP_LABELS: Record<string, string> = {
  [ReporterRelationship.POLICYHOLDER_OWNER]: 'Policyholder',
  [ReporterRelationship.DRIVER]: 'Driver',
  [ReporterRelationship.PASSENGER]: 'Passenger',
  [ReporterRelationship.FLEET_MANAGER]: 'Fleet manager',
  [ReporterRelationship.REPORTER_ONLY]: 'Reporter only',
}

export function joinLabels(values: string[] | null, labels: Record<string, string>): string {
  return (values ?? []).map((v) => labels[v] ?? v).join(' · ')
}

/** "Jun 8, 2026" */
export function formatIncidentDate(date: string | null): string {
  if (!date) return ''
  return new Date(`${date}T00:00:00`).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })
}

/** "9:14 AM" */
export function formatIncidentTime(time: string | null): string {
  if (!time) return ''
  const [h, m] = time.split(':')
  const d = new Date()
  d.setHours(Number(h), Number(m), 0, 0)
  return d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })
}

/** "June 9, 2026" — full month, used for the read-only certification date. */
export function formatFullDate(date: Date): string {
  return date.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })
}

export function formatTruck(truck: ClaimTruck | null): string {
  if (!truck) return '—'
  const desc = [truck.year, truck.make, truck.model].filter(Boolean).join(' ')
  const vin = truck.vin ? ` · VIN ${truck.vin}` : ''
  const plate = truck.licensePlate ? ` · Plate ${truck.licensePlate}` : ''
  if (!desc && !vin && !plate) {
    // A phone claim often has only the unit number the caller said.
    return truck.unitNumber ? `Unit ${truck.unitNumber}` : 'Truck'
  }
  return `${desc || 'Truck'}${vin}${plate}`
}

export function formatTrailer(attached: boolean | null, trailer: ClaimTrailer | null): string {
  if (attached !== true) return 'No'
  const parts = ['Yes']
  if (trailer?.model) parts.push(trailer.model)
  if (trailer?.unitNumber && !trailer.vin && !trailer.licensePlate) parts.push(`Unit ${trailer.unitNumber}`)
  if (trailer?.vin) parts.push(`VIN ${trailer.vin}`)
  if (trailer?.licensePlate) parts.push(`Plate ${trailer.licensePlate}`)
  if (trailer?.trailerOwnership) parts.push(TRAILER_OWNERSHIP_LABELS[trailer.trailerOwnership] ?? '')
  return parts.filter(Boolean).join(' · ')
}

/** "Acme Insurance · Policy ACM-4471902" */
export function formatOtherInsurance(vehicle: ClaimOtherVehicle | null): string {
  const policy = vehicle?.policyNumber ? `Policy ${vehicle.policyNumber}` : ''
  return [vehicle?.insuranceCompany, policy].filter(Boolean).join(' · ') || '—'
}

export function formatDriver(driver: ClaimDriverInfo | null): string {
  if (!driver) return '—'
  const name = `${driver.firstName} ${driver.lastName}`.trim()
  const cdl = driver.cdlNumber ? ` · CDL ${driver.cdlNumber}` : ''
  return `${name || 'Driver'}${cdl}`
}

export function formatReporterRelationships(rels: ReporterRelationship[]): string {
  return rels.map((r) => REPORTER_RELATIONSHIP_LABELS[r] ?? r).join(' · ')
}
